import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import { CHAT_CART_ORDER_STATUS_LABELS, type ChatCartOrder } from '@/types/chatCartOrder';
import { isValidUUID } from '@/utils/uuid';
import styles from './CartOrderDetailPage.module.scss';

export default function CartOrderDetailPage() {
  const { cartOrderId } = useParams<{ cartOrderId: string }>();
  const navigate = useNavigate();
  const [cartOrder, setCartOrder] = useState<ChatCartOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const id = cartOrderId && cartOrderId.trim();
    if (!id || !isValidUUID(id)) {
      setError('Invalid order.');
      setLoading(false);
      return;
    }
    apiService.getCartOrderDetail(id).then((res: any) => {
      const data = res && res.data !== undefined ? res.data : res;
      const order = data && data.cartOrder;
      if (!order || !order.id) {
        setError('Order not found.');
        setLoading(false);
        return;
      }
      setCartOrder(order);
      setLoading(false);
    }).catch(() => {
      setError('Failed to load order.');
      setLoading(false);
    });
  }, [cartOrderId]);

  const handleBack = () => navigate(-1);

  if (!cartOrderId || !cartOrderId.trim() || !isValidUUID(cartOrderId.trim())) {
    return (
      <>
        <DashboardHeader />
        <div className={styles.container}>
          <button type="button" className={styles.backButton} onClick={handleBack}>← Back</button>
          <p className={styles.error}>{error || 'Invalid order.'}</p>
          <button type="button" className={styles.primaryButton} onClick={() => navigate('/customer/organizers')}>Back to Organizers</button>
        </div>
        <Footer />
      </>
    );
  }

  if (loading) {
    return (
      <>
        <DashboardHeader />
        <div className={styles.container}>
          <button type="button" className={styles.backButton} onClick={handleBack}>← Back</button>
          <p>Loading order...</p>
        </div>
        <Footer />
      </>
    );
  }

  if (error || !cartOrder) {
    return (
      <>
        <DashboardHeader />
        <div className={styles.container}>
          <button type="button" className={styles.backButton} onClick={handleBack}>← Back</button>
          <p className={styles.error}>{error || 'Order not found.'}</p>
        </div>
        <Footer />
      </>
    );
  }

  const items = cartOrder.items || [];
  const statusLabel = CHAT_CART_ORDER_STATUS_LABELS[cartOrder.status as keyof typeof CHAT_CART_ORDER_STATUS_LABELS] || cartOrder.status;
  const subtotal = items.reduce((sum, i) => sum + (Number(i.unitPrice) || 0) * (i.quantity || 0), 0);
  const deliverBy = cartOrder.deliverBy ? new Date(cartOrder.deliverBy).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' }) : null;

  return (
    <>
      <DashboardHeader />
      <div className={styles.container}>
        <button type="button" className={styles.backButton} onClick={handleBack}>← Back</button>
        <h1 className={styles.title}>Cart order</h1>
        <div className={styles.card}>
          <div className={styles.meta}>
            <span className={styles.statusBadge} data-status={cartOrder.status}>{statusLabel}</span>
            <span className={styles.date}>Created {new Date(cartOrder.createdAt).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })}</span>
            {cartOrder.branch && cartOrder.branch.name && <span className={styles.branch}>{cartOrder.branch.name}</span>}
          </div>
          {deliverBy && <p className={styles.deliverBy}>Deliver by: {deliverBy}</p>}
          {cartOrder.organizerNotes && <p className={styles.notes}>Note: {cartOrder.organizerNotes}</p>}
          <h2 className={styles.itemsTitle}>Items</h2>
          <ul className={styles.itemsList}>
            {items.map((item) => {
              const lineTotal = (Number(item.unitPrice) || 0) * (item.quantity || 0);
              const qty = item.quantityText ? item.quantityText : item.quantity ? `x ${item.quantity}` : '';
              return (
                <li key={item.id} className={styles.itemRow}>
                  <span className={styles.itemName}>
                    {item.itemName}{qty ? ` ${qty}` : ''}{item.note ? ` · ${item.note}` : ''}
                  </span>
                  <span className={styles.itemPrice}>{lineTotal > 0 ? `Rs.${lineTotal}` : '—'}</span>
                </li>
              );
            })}
          </ul>
          <p className={styles.subtotal}>Subtotal: ₹{subtotal}</p>
        </div>
      </div>
      <Footer />
    </>
  );
}
